import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import { useEffect, useState, useRef } from "react";
import PermissionErrorPage from "../shared/permissionError";
import { userService } from "../../services/api";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import '../../styles/components/ui/tables.css';

export default function Posts() {
  const [studentUsernames, setStudentUsernames] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const batchSize = 50; // Load more students at once for table view
  const batchIndexRef = useRef(0);

  const fetchStudentData = async (username) => {
    try {
      const studentData = await userService.getStudentByUsername(username);
      return {
        username: username,
        studentID: studentData?.studentID ?? null,
        firstName: studentData?.firstName ?? "N/A",
        lastName: studentData?.lastName ?? "N/A",
        email: studentData?.email ?? "N/A"
      };
    } catch (error) {
      console.error("Error fetching student data for username:", username, error);
      return {
        username: username,
        studentID: null,
        firstName: "N/A",
        lastName: "N/A",
        email: "N/A"
      };
    }
  };

  useEffect(() => {
    const fetchStudentUsernames = async () => {
      setInitialLoading(true);
      try {
        const usernames = await userService.getUsernames();
        setStudentUsernames(Array.isArray(usernames) ? usernames : []);
        batchIndexRef.current = 0;
        setStudents([]);
        setHasMore(Array.isArray(usernames) && usernames.length > 0);
      } catch (error) {
        console.error("Error fetching student usernames:", error);
        setStudentUsernames([]);
        setHasMore(false);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchStudentUsernames();
  }, []);

  const loadNextBatch = async () => {
    if (loading) return;
    setLoading(true);

    const start = batchIndexRef.current * batchSize;
    const end = start + batchSize;
    const slice = studentUsernames.slice(start, end);

    if (slice.length === 0) {
      setHasMore(false);
      setLoading(false);
      return;
    }

    try {
      const batch = await Promise.all(
        slice.map(async (username) => {
          return await fetchStudentData(username);
        })
      );

      batchIndexRef.current += 1;
      // Sort by studentID (GTID) before adding to state
      const sortedBatch = batch.sort((a, b) => {
        const idA = a.studentID ?? Number.MAX_SAFE_INTEGER;
        const idB = b.studentID ?? Number.MAX_SAFE_INTEGER;
        return idA - idB;
      });
      
      setStudents((prev) => {
        const combined = [...prev, ...sortedBatch];
        // Re-sort the entire array to maintain order
        return combined.sort((a, b) => {
          const idA = a.studentID ?? Number.MAX_SAFE_INTEGER;
          const idB = b.studentID ?? Number.MAX_SAFE_INTEGER;
          return idA - idB;
        });
      });

      if (end >= studentUsernames.length) setHasMore(false);
    } catch (e) {
      console.error("Error loading batch:", e);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentUsernames.length > 0 && !initialLoading) {
      loadNextBatch();
    }
  }, [studentUsernames, initialLoading]);

  if (localStorage.getItem("roleData") !== "instructor") {
    return <PermissionErrorPage />;
  }

  return (
    <div className="table-container">
      <div className="table-header">
        <h1 className="table-page-title">Current Students</h1>
        <p className="table-page-subtitle">View all registered students with their ID and course information.</p>
      </div>

      <div className="table-wrapper">
        {initialLoading ? (
          <LoadingSpinner text="Loading students..." />
        ) : (
          <>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Username</th>
                    <th>GTID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="6" style={{textAlign: 'center', padding: '2rem', color: '#666'}}>
                        No students found.
                      </td>
                    </tr>
                  ) : (
                    students.map((s, idx) => (
                      <tr key={`${s.username}-${idx}`}>
                        <td>{idx + 1}</td>
                        <td>{s.username ?? "N/A"}</td>
                        <td>{s.studentID ?? "N/A"}</td>
                        <td>{s.firstName}</td>
                        <td>{s.lastName}</td>
                        <td>{s.email}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="table-load-more">
              {hasMore ? (
                <Button
                  onClick={loadNextBatch}
                  disabled={loading}
                  className="table-load-more-button"
                  variant="outline-primary"
                >
                  {loading ? "Loading..." : `Load More Students (${students.length} of ${studentUsernames.length})`}
                </Button>
              ) : students.length > 0 ? (
                <p className="table-load-more-status">All {students.length} students loaded.</p>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}